export type LegalDocument = {
  id: 'privacidad' | 'terminos'
  title: string
  updated: string
  intro: string
  sections: { heading: string; body: string[] }[]
}

export const legalDocuments: LegalDocument[] = [
  {
    id: 'privacidad',
    title: 'Política de Privacidad',
    updated: 'Última actualización: enero de 2024',
    intro:
      'Esta política explica qué datos se recopilan cuando visitas esta página o adquieres la guía digital Vientre Plano Express, con qué finalidad se usan y qué control tienes sobre ellos.',
    sections: [
      {
        heading: '1. Datos que se recopilan',
        body: [
          'Datos que tú proporcionas: el nombre y el correo electrónico que escribes al realizar la compra, necesarios únicamente para entregarte el archivo digital y el comprobante correspondiente.',
          'Datos técnicos recopilados automáticamente: dirección IP aproximada, tipo de dispositivo, navegador, páginas vistas y tiempo de permanencia. Estos datos son estadísticos y no permiten identificarte personalmente.',
        ],
      },
      {
        heading: '2. Datos de pago',
        body: [
          'El pago se procesa íntegramente a través de una plataforma de pagos externa. Los números de tarjeta y credenciales bancarias nunca se transmiten ni se almacenan en este sitio; solo se recibe la confirmación de que la transacción fue aprobada.',
        ],
      },
      {
        heading: '3. Finalidad del tratamiento',
        body: [
          'Los datos se utilizan exclusivamente para: entregar el producto adquirido, atender solicitudes relacionadas con la compra y la garantía, cumplir obligaciones fiscales y legales, y medir de forma agregada el rendimiento de la página.',
          'No se utilizan tus datos para tomar decisiones automatizadas ni para elaborar perfiles de salud.',
        ],
      },
      {
        heading: '4. Comunicaciones',
        body: [
          'Tras la compra recibirás los correos imprescindibles sobre tu pedido. Si además aceptas recibir contenido informativo, puedes cancelar esa suscripción en cualquier momento desde el enlace incluido al final de cada envío, sin que ello afecte tu acceso a la guía.',
        ],
      },
      {
        heading: '5. Cookies y tecnologías similares',
        body: [
          'Se utilizan cookies técnicas necesarias para el funcionamiento del sitio y cookies analíticas para entender de forma anónima cómo se navega la página. Puedes bloquearlas o eliminarlas desde la configuración de tu navegador; el sitio seguirá funcionando, aunque algunas funciones podrían verse limitadas.',
        ],
      },
      {
        heading: '6. Compartición con terceros',
        body: [
          'No se vende, alquila ni cede tu información personal. Solo se comparte con los proveedores estrictamente necesarios para operar: la plataforma de pagos, el servicio de envío de correo y el proveedor de alojamiento, cada uno limitado a la finalidad indicada.',
        ],
      },
      {
        heading: '7. Conservación y seguridad',
        body: [
          'Los datos de compra se conservan durante el plazo exigido por la normativa fiscal aplicable y después se eliminan o anonimizan. Se aplican medidas técnicas razonables, incluida la transmisión cifrada, para proteger la información frente a accesos no autorizados.',
        ],
      },
      {
        heading: '8. Tus derechos',
        body: [
          'Puedes solicitar en cualquier momento el acceso, la rectificación, la eliminación o la portabilidad de tus datos, así como oponerte a su tratamiento o retirar tu consentimiento. Las solicitudes se atienden a través del mismo canal por el que recibiste tu pedido.',
        ],
      },
      {
        heading: '9. Menores de edad',
        body: [
          'Este producto está dirigido a personas mayores de 18 años. No se recopila de forma intencional información de menores de edad.',
        ],
      },
      {
        heading: '10. Cambios en esta política',
        body: [
          'Esta política puede actualizarse para reflejar cambios legales u operativos. La fecha de la última actualización siempre aparece al inicio del documento.',
        ],
      },
    ],
  },
  {
    id: 'terminos',
    title: 'Términos y Condiciones',
    updated: 'Última actualización: enero de 2024',
    intro:
      'Al adquirir o utilizar la guía digital Vientre Plano Express aceptas los siguientes términos. Te recomendamos leerlos antes de completar la compra.',
    sections: [
      {
        heading: '1. Objeto',
        body: [
          'El producto es una guía digital en formato PDF de carácter informativo y educativo. La compra otorga una licencia de uso personal, individual e intransferible.',
        ],
      },
      {
        heading: '2. Naturaleza informativa del contenido',
        body: [
          'El contenido no constituye consejo médico, diagnóstico ni tratamiento, y no sustituye la valoración de un profesional de la salud cualificado. Debes consultar a tu médico antes de modificar tu alimentación, tu hidratación o tu rutina de ejercicio, especialmente si estás embarazada, en período de lactancia, bajo tratamiento farmacológico o con alguna condición médica diagnosticada.',
        ],
      },
      {
        heading: '3. Ausencia de garantía de resultados',
        body: [
          'Los resultados descritos, incluidos los testimonios, son experiencias individuales y no representan una promesa de resultado. Los resultados varían según la persona, sus hábitos, su constancia y su estado de salud. No se garantiza ningún resultado específico ni en un plazo determinado.',
        ],
      },
      {
        heading: '4. Precio, pago y entrega',
        body: [
          'El precio indicado corresponde a un pago único, sin suscripciones ni cargos recurrentes. Una vez confirmado el pago, el acceso de descarga se entrega de forma digital e inmediata. La entrega puede depender de factores ajenos, como filtros de correo o errores al escribir la dirección electrónica.',
        ],
      },
      {
        heading: '5. Garantía y reembolsos',
        body: [
          'Existe una garantía de satisfacción de 7 días desde la compra. Si aplicaste el protocolo y no estás conforme, puedes solicitar la devolución del importe íntegro dentro de ese plazo mediante el canal de atención asociado a tu pedido. Tras el reembolso, la licencia de uso queda revocada y debes dejar de utilizar el material.',
        ],
      },
      {
        heading: '6. Propiedad intelectual',
        body: [
          'Todo el contenido —textos, estructura, protocolos, imágenes y diseño— está protegido por derechos de autor. Queda prohibida su reproducción total o parcial, su reventa, su distribución, su publicación en cualquier medio o plataforma y la creación de obras derivadas sin autorización previa por escrito.',
        ],
      },
      {
        heading: '7. Uso permitido',
        body: [
          'Puedes descargar el archivo y conservar copias para tu consulta personal. No puedes compartir tu acceso, publicar el archivo en repositorios, grupos o redes, ni utilizar el contenido con fines comerciales o formativos frente a terceros.',
        ],
      },
      {
        heading: '8. Limitación de responsabilidad',
        body: [
          'El autor y los editores no se hacen responsables del uso que se dé a esta información ni de los daños directos o indirectos derivados de su aplicación. Al utilizar el material, asumes la responsabilidad total de tus acciones y decisiones de salud.',
        ],
      },
      {
        heading: '9. Disponibilidad del sitio',
        body: [
          'Se procura mantener la página disponible de forma continua, pero pueden producirse interrupciones por mantenimiento o causas técnicas ajenas. Estas interrupciones no afectan el derecho de acceso al producto ya adquirido.',
        ],
      },
      {
        heading: '10. Modificaciones y legislación aplicable',
        body: [
          'Estos términos pueden actualizarse; la versión vigente es la publicada en esta página en el momento de la compra. La relación se rige por la legislación aplicable en el domicilio del titular del producto, sin perjuicio de los derechos que la normativa de consumo reconozca al comprador.',
        ],
      },
    ],
  },
]
